import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Subscribe to Postgres row-level changes on a table.
 * @param {string} table - Table name
 * @param {string} event - 'INSERT' | 'UPDATE' | 'DELETE' | '*'
 * @param {Function} callback - (payload) => void
 * @param {object} filter - Optional filter, e.g. { column: 'event_id', value: '...' }
 */
export function usePostgresChanges(table, event, callback, filter) {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    const channelName = `pg-${table}-${event}-${filter?.column || 'all'}-${filter?.value || 'all'}`;
    const channelConfig = {
      event,
      schema: 'public',
      table,
    };
    if (filter?.column && filter?.value) {
      channelConfig.filter = `${filter.column}=eq.${filter.value}`;
    }

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', channelConfig, (payload) => {
        cbRef.current(payload);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [table, event, filter?.column, filter?.value]);
}

/**
 * Subscribe to a Supabase Broadcast channel for ephemeral messages.
 * @param {string} channelName
 * @param {string} eventName
 * @param {Function} callback
 */
export function useBroadcast(channelName, eventName, callback) {
  const cbRef = useRef(callback);
  const channelRef = useRef(null);
  cbRef.current = callback;

  useEffect(() => {
    const channel = supabase
      .channel(channelName)
      .on('broadcast', { event: eventName }, (payload) => {
        cbRef.current(payload.payload);
      })
      .subscribe();
    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, [channelName, eventName]);

  const send = useCallback((data) => {
    channelRef.current?.send({
      type: 'broadcast',
      event: eventName,
      payload: data,
    });
  }, [eventName]);

  return send;
}

/**
 * Track presence on a channel (online users, viewer counts).
 * @param {string} channelName
 * @param {object} userMeta - { user_id, user_name, ... }
 * @returns {{ presenceState: object[], count: number }}
 */
export function usePresence(channelName, userMeta) {
  const [presenceState, setPresenceState] = useState([]);
  const metaRef = useRef(userMeta);
  metaRef.current = userMeta;

  useEffect(() => {
    if (!channelName) return;

    const channel = supabase.channel(channelName, {
      config: { presence: { key: metaRef.current?.user_id || 'anon' } },
    });

    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      const users = Object.values(state).flat();
      setPresenceState(users);
    });

    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track(metaRef.current || { role: 'viewer' });
      }
    });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [channelName]);

  return { presenceState, count: presenceState.length };
}

/**
 * Combined channel with broadcast + presence for draft events.
 * @param {string} eventId
 * @param {object} userMeta
 * @returns {{ sendBroadcast, viewers, viewerCount }}
 */
export function useDraftEventChannel(eventId, userMeta) {
  const [viewers, setViewers] = useState([]);
  const channelRef = useRef(null);
  const broadcastCallbacks = useRef({});

  useEffect(() => {
    if (!eventId) return;

    const channel = supabase.channel(`draft-event-${eventId}`, {
      config: { presence: { key: userMeta?.user_id || `anon-${Date.now()}` } },
    });

    // Presence sync
    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      const users = Object.values(state).flat();
      setViewers(users);
    });

    // Broadcast handlers
    channel.on('broadcast', { event: 'reaction' }, (payload) => {
      broadcastCallbacks.current['reaction']?.(payload.payload);
    });

    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track(userMeta || { role: 'viewer' });
      }
    });

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, userMeta?.user_id]);

  const sendBroadcast = useCallback((event, data) => {
    channelRef.current?.send({
      type: 'broadcast',
      event,
      payload: data,
    });
  }, []);

  const onBroadcast = useCallback((event, cb) => {
    broadcastCallbacks.current[event] = cb;
  }, []);

  return {
    sendBroadcast,
    onBroadcast,
    viewers,
    viewerCount: viewers.length,
  };
}
